import { useTranslations } from "next-intl";

/**
 * Minimal translator shape used by `formatAccountTypeLabel`. Structurally
 * compatible with the function returned by `useTranslations("enums")` — we only
 * use the call signature and the `.has()` method, so we avoid pulling in
 * next-intl's narrow key types and keep the helper testable in isolation
 * (no React render, no DOM, no @testing-library/react). Mirrors
 * `formatPurposeCode.ts`.
 */
type EnumTranslator = {
  (key: string): string;
  has: (key: string) => boolean;
};

/**
 * Pure helper: resolves a backend CustomerAccount `accountType` value to its
 * translated label under the `enums.CustomerAccountType.*` namespace, falling
 * back to the raw value when the translation is missing — e.g. the backend
 * introduces a new enum value (TREASURY) before the dictionary catches up.
 *
 * Without this guard, `tEnums("CustomerAccountType.TREASURY")` resolves a
 * missing key, `getMessageFallback` renders a blank string and reports an
 * `[i18n] MISSING_MESSAGE` Sentry warning (CUSTOMER-UI-6Q). The `t.has()` guard
 * is the canonical "dynamic translation keys" fix — see
 * `.claude/rules/web/error-boundaries.md` and `.claude/docs/incident-log.md`.
 *
 * Extracted so the test suite can exercise it without rendering a React tree
 * (see `formatAccountType.test.ts`).
 */
export function formatAccountTypeLabel(
  value: string | undefined,
  tEnums: EnumTranslator
): string | null {
  if (!value) return null;
  const key = `CustomerAccountType.${value}`;
  return tEnums.has(key) ? tEnums(key) : value;
}

export function useAccountTypeLabel() {
  const tEnums = useTranslations("enums");
  return (value: string | undefined): string | null =>
    formatAccountTypeLabel(value, tEnums as EnumTranslator);
}
